"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PrivacyPage = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/* ---------- PrivacyPage.tsx ---------- */
const material_1 = require("@mui/material");
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const log_view_layout_1 = require("../../component/log-view-layout");
const log_1 = require("../../model/log");
const SectionHTML = ({ html }) => ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
        "& p": { m: 0, mb: 2, color: "text.secondary", fontSize: 16 },
        "& li": { color: "text.secondary", fontSize: 16 },
        "& h3, & h4": { mt: 2, mb: 1, fontWeight: "bold" },
        "& a": { color: "primary.main" },
        "& table": {
            width: "100%",
            borderCollapse: "collapse",
            margin: "1em 0",
        },
        "& th, & td": {
            border: "1px solid #ddd",
            padding: "8px",
            textAlign: "left",
        },
        "& th": {
            backgroundColor: "#f2f2f2",
            fontWeight: "bold",
        },
    }, dangerouslySetInnerHTML: { __html: html } }));
const htmlSections = {
    intro: `
    <p>
      This Privacy Notice for Litly, LLC (“we,” “us,” or “our”) describes how and why we might access,
      collect, store, use, and/or share (“process”) your personal information when you use our services
      (“Services”), including when you:
    </p>
    <ul>
      <li>
        Visit our website at
        litlyai.com and related subdomains
        or any website of ours that links to this Privacy Notice
      </li>
      <li>
        Use Litly AI. Our platform curates engaging topics aligned with recognized language learning
        frameworks. Using AI, we generate personalized reading materials tailored to each user’s
        proficiency level. To reinforce learning, we provide interactive quizzes designed to help users
        progressively master the language.
      </li>
      <li>
        Engage with us in other related ways, including any sales, marketing, or events
      </li>
    </ul>
    <p>
      <strong>Questions or concerns?</strong>
      Reading this Privacy Notice will help you understand your privacy rights and choices. We are
      responsible for deciding how your personal information is processed. If you do not agree with our
      policies and practices, please do not use our Services. If you still have any questions or
      concerns, contact us at
      <a href="mailto:admin@litlyai.com">admin@litlyai.com</a>.
    </p>
  `,
    summary: `
    <p>
      <strong><em>This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our</em></strong>
      <a href="#toc"><strong><em>table of contents</em></strong></a>
      <strong><em>below to find the section you are looking for.</em></strong>
    </p>
    <p>
      <strong>What personal information do we process?</strong>
      When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use.
      Learn more about <a href="#personalinfo">personal information you disclose to us</a>.
    </p>
    <p>
      <strong>Do we process any sensitive personal information?</strong>
      Some of the information may be considered "special" or "sensitive" in certain jurisdictions, for example your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive personal information.
    </p>
    <p>
      <strong>Do we collect any information from third parties?</strong>
      We do not collect any information from third parties.
    </p>
    <p>
      <strong>How do we process your information?</strong>
      We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so. Learn more about <a href="#infouse">how we process your information</a>.
    </p>
    <p>
      <strong>In what situations and with which parties do we share personal information?</strong>
      We may share information in specific situations and with specific third parties. Learn more about <a href="#whoshare">when and with whom we share your personal information</a>.
    </p>
    <p>
      <strong>How do we keep your information safe?</strong>
      We have adequate organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Learn more about <a href="#infosafe">how we keep your information safe</a>.
    </p>
    <p>
      <strong>What are your rights?</strong>
      Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information. Learn more about <a href="#privacyrights">your privacy rights</a>.
    </p>
    <p>
      <strong>How do you exercise your rights?</strong>
      The easiest way to exercise your rights is by <a href="mailto:admin@litlyai.com">contacting us</a>. We will consider and act upon any request in accordance with applicable data protection laws.
    </p>
    <p>
      Want to learn more about what we do with any information we collect?<a href="#toc">Review the Privacy Notice in full</a>.
    </p>
  `,
    infocollect: `
    <p><strong><em>In Short:</em></strong><em>We collect personal information that you provide to us.</em></p>
    <p>
      We collect personal information that you voluntarily provide to us when you
      register on the Services,express an interest in obtaining information about
      us or our products and Services, when you participate in activities on the
      Services, or otherwise when you contact us.
    </p>
    <p>
      <strong>Personal Information Provided by You.</strong> The personal
      information that we collect depends on the context of your interactions with
      us and the Services, the choices you make, and the products and features you
      use. The personal information we collect may include the following:
    </p>
    <ul>
      <li>email addresses</li>
      <li>usernames</li>
      <li>passwords</li>
      <li>reading history</li>
      <li>quiz history</li>
      <li>purchase history</li>
      <li>reading level</li>
    </ul>
    <p id="sensitiveinfo"><strong>Sensitive Information.</strong> We do not process sensitive information.</p>
    <p>
      All personal information that you provide to us must be true, complete, and
      accurate, and you must notify us of any changes to such personal information.
    </p>
  `,
    infouse: `
    <p>
      <strong><em>In Short:</em></strong>
      <em>We process your information to provide, improve, and administer our Services,
      communicate with you, for security and fraud prevention, and to comply with
      law. We process the personal information for the following purposes listed
      below. We may also process your information for other purposes only with
      your prior explicit&nbsp;consent.</em>
    </p>
    <p>
      <strong>We process your personal information for a variety of reasons,
      depending on how you interact with our Services, including:</strong>
    </p>
    <ul>
      <li>
        <strong>To facilitate account creation and authentication and otherwise
        manage user accounts.</strong>
        We may process your information so you can create and log in to your
        account, as well as keep your account in working order.
      </li>
      <li>
        <strong>To deliver and facilitate delivery of services to the user.</strong>
        We may process your information to provide you with the requested service.
      </li>
      <li>
        <strong>To save or protect an individual's vital interest.</strong>
        We may process your information when necessary to save or protect an
        individuals vital interest, such as to prevent harm.
      </li>
      <li>
        <strong>Reading history, Quiz history, and Reading level</strong>.
        To make sure we provide the accurate service to user
      </li>
    </ul>
  `,
    legalbases: `
    <p><em><strong>In Short:</strong>We only process your personal information when we believe it is
    necessary and we have a valid legal reason (i.e. , legal basis) to do so
    under applicable law, like with your consent, to comply with laws, to provide
    you with services to enter into or fulfill our contractual obligations, to
    protect your rights, or to fulfill our legitimate business interests.</em></p>
    <p><em><strong><u>If you are located in the EU or UK, this section applies to you.</u></strong></em></p>
    <p>The General Data Protection Regulation (GDPR) and UK GDPR require us to
    explain the valid legal bases we rely on in order to process your personal
    information. As such, we may rely on the following legal bases to process
    your personal information:</p>
    <ul>
      <li><strong>Consent.</strong>We may process your information if you have given us permission (i.e. , consent) to use your personal information
          for a specific purpose. You can withdraw your consent at any time. Learn
          more about <a href="#withdrawconsent">withdrawing your consent</a>.</li>
      <li><strong>Performance of a Contract.</strong> We may process your personal
          information when we believe it is necessary to fulfill our contractual
          obligations to you, including providing our Services or at your request
          prior to entering into a contract with you.</li>
      <li><strong>Legal Obligations.</strong> We may process your information
          where we believe it is necessary for compliance with our legal
          obligations, such as to cooperate with a law enforcement body or
          regulatory agency, exercise or defend our legal rights, or disclose your
          information as evidence in litigation in which we are involved.</li>
      <li><strong>Vital Interests.</strong> We may process your information where
          we believe it is necessary to protect your vital interests or the vital
          interests of a third party, such as situations involving potential
          threats to the safety of any person.</li>
    </ul>
    <p><em><strong><u>If you are located in Canada, this section applies to you.</u></strong></em></p>
    <p>We may process your information if you have given us specific permission
    (i.e. , express consent) to use your personal information for a specific
    purpose, or in situations where your permission can be inferred (i.e. ,
    implied consent). You can <a href="#withdrawconsent">withdraw your consent</a> at any time.</p>
    <p>In some exceptional cases, we may be legally permitted under applicable law
    to process your information without your consent, including, for example:</p>
    <ul>
      <li>If collection is clearly in the interests of an individual and consent
          cannot be obtained in a timely way</li>
      <li>For investigations and fraud detection and prevention</li>
      <li>For business transactions provided certain conditions are met</li>
      <li>If it is contained in a witness statement and the collection is
          necessary to assess, process, or settle an insurance claim</li>
      <li>For identifying injured, ill, or deceased persons and communicating with
          next of kin</li>
      <li>If we have reasonable grounds to believe an individual has been, is, or
          may be victim of financial abuse</li>
      <li>If it is reasonable to expect collection and use with consent would
          compromise the availability or the accuracy of the information and the
          collection is reasonable for purposes related to investigating a breach
          of an agreement or a contravention of the laws of Canada or a province</li>
      <li>If disclosure is required to comply with a subpoena, warrant, court
          order, or rules of the court relating to the production of records</li>
      <li>If it was produced by an individual in the course of their employment,
          business, or profession and the collection is consistent with the
          purposes for which the information was produced</li>
      <li>If the collection is solely for journalistic, artistic, or literary
          purposes</li>
      <li>If the information is publicly available and is specified by the
          regulations</li>
      <li>We may disclose de-identified information for approved research or
          statistics projects, subject to ethics oversight and confidentiality
          commitments</li>
    </ul>
  `,
    whoshare: `
    <p>
      <strong><em>In Short:</em></strong><em>We may share information in specific
      situations described in this section and/or with the following third
      parties.</em>
    </p>
    <p>
      We may need to share your personal information in the following situations:
    </p>
    <ul>
      <li>
        <strong>Business Transfers.</strong> We may share or transfer your
        information in connection with, or during negotiations of, any merger,
        sale of company assets, financing, or acquisition of all or a portion of
        our business to another company.
      </li>
    </ul>
  `,
    cookies: `
    <p>
      <strong><em>In Short:</em></strong><em>We may use cookies and other tracking
      technologies to collect and store your information.</em>
    </p>
    <p>
      We may use cookies and similar tracking technologies (like web beacons and
      pixels) to gather information when you interact with our Services. Some
      online tracking technologies help us maintain the security of our Services
      and your account, prevent crashes, fix bugs, save your preferences, and
      assist with basic site functions.
    </p>
    <p>
      We also permit third parties and service providers to use online tracking
      technologies on our Services for analytics and advertising, including to
      help manage and display advertisements, to tailor advertisements to your
      interests, or to send abandoned shopping cart reminders (depending on your
      communication preferences). The third parties and service providers use
      their technology to provide advertising about products and services tailored
      to your interests which may appear either on our Services or on other
      websites.
    </p>
    <p>
      To the extent these online tracking technologies are deemed to be a
      <em>"sale"/"sharing"</em> (which includes targeted advertising, as defined
      under the applicable laws) under applicable US state laws, you can opt out
      of these online tracking technologies by submitting a request as described
      below under section "<a href="#uslaws">DO UNITED STATES RESIDENTS HAVE
      SPECIFIC PRIVACY RIGHTS?</a>".
    </p>
    <p>
      Specific information about how we use such technologies and how you can
      refuse certain cookies is set out in our Cookie Notice:
      <a href="/Cookie.html" target="_blank">
        http://litlyai.com/cookiepolicy
      </a>.
    </p>
  `,
    ai: `
    <p>
      <strong><em>In Short:</em></strong><em>We offer products, features, or tools
      powered by artificial intelligence, machine learning, or similar
      technologies.</em>
    </p>
    <p>
      As part of our Services, we offer products, features, or tools powered by
      artificial intelligence, machine learning, or similar technologies
      (collectively, "AI Products"). These tools are designed to enhance your
      experience and provide you with innovative solutions. The terms in this
      Privacy Notice govern your use of the AI Products within our Services.
    </p>
    <p><strong>Our AI Products</strong></p>
    <p>Our AI Products are designed for the following functions:</p>
    <ul>
      <li>AI document generation</li>
      <li>Image generation</li>
    </ul>
    <p><strong>How We Process Your Data Using AI</strong></p>
    <p>
      All personal information processed using our AI Products is handled in line
      with our Privacy Notice and our agreement with third parties. This ensures
      high security and safeguards your personal information throughout the
      process, giving you peace of mind about your data's safety.
    </p>
  `,
    inforetain: `
    <p>
      <strong><em>In Short:</em></strong><em>We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.</em>
    </p>
    <p>
      We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this notice will require us keeping your personal information for longer than three (3) months past the start of the idle period of the user's account.
    </p>
    <p>
      When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
    </p>
  `,
    infosafe: `
    <p>
      <strong><em>In Short:</em></strong>
      <em>We aim to protect your personal information through a system of organizational and technical security measures.</em>
    </p>
    <p>
      We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Services is at your own risk. You should only access the Services within a secure environment.
    </p>
  `,
    infominors: `
    <p>
      <strong><em>In Short:</em></strong><em>We do not knowingly collect data from or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction.</em>
    </p>
    <p>
      We do not knowingly collect, solicit data from, or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 or the equivalent age as specified by law in your jurisdiction or that you are the parent or guardian of such a minor and consent to such minor dependent’s use of the Services. If we learn that personal information from users less than 18 years of age or the equivalent age as specified by law in your jurisdiction has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18 or the equivalent age as specified by law in your jurisdiction, please contact us at dpo@litlyai.com.
    </p>
  `,
    privacyrights: `
    <p>
      <strong><em>In Short:</em></strong><em> Depending on your state of residence in the US or in some regions,
      such as the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that
      allow you greater access to and control over your personal information. You may review, change, or terminate
      your account at any time, depending on your country, province, or state of residence.</em>
    </p>
    <p>
      In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under applicable data
      protection laws. These may include the right (i) to request access and obtain a copy of your personal
      information, (ii) to request rectification or erasure; (iii) to restrict the processing of your personal
      information; (iv) if applicable, to data portability; and (v) not to be subject to automated
      decision-making. If a decision that produces legal or similarly significant effects is made solely by
      automated means, we will inform you, explain the main factors, and offer a simple way to request human
      review. In certain circumstances, you may also have the right to object to the processing of your personal
      information. You can make such a request by contacting us by using the contact details provided in the
      section “HOW CAN YOU CONTACT US ABOUT THIS NOTICE?” below.
    </p>
    <p>
      We will consider and act upon any request in accordance with applicable data protection laws.
    </p>
    <p>
      If you are located in the EEA or UK and you believe we are unlawfully processing your personal information,
      you also have the right to complain to your <a
        href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm" target="_blank">Member
        State data protection authority</a> or the <a
        href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/"
        target="_blank">UK data protection authority</a>.
    </p>
    <p>
      If you are located in Switzerland, you may contact the <a href="https://www.edoeb.admin.ch/edoeb/en/home.html"
        target="_blank">Federal Data Protection and Information Commissioner</a>.
    </p>
    <h3 id="withdrawconsent"><u>Withdrawing your consent:</u></h3>
    <p>
      If we are relying on your consent to process your personal information, which may be express and/or implied
      consent depending on the applicable law, you have the right to withdraw your consent at any time. You can
      withdraw your consent at any time by contacting us by using the contact details provided in the section “HOW
      CAN YOU CONTACT US ABOUT THIS NOTICE?” below.
    </p>
    <p>
      However, please note that this will not affect the lawfulness of the processing before its withdrawal nor,
      when applicable law allows, will it affect the processing of your personal information conducted in
      reliance on lawful processing grounds other than consent.
    </p>
    <h3>Account Information</h3>
    <p>
      If you would at any time like to review or change the information in your account or terminate your account,
      you can:
    </p>
    <ul>
      <li>Contact us using the contact information provided.</li>
    </ul>
    <p>
      Upon your request to terminate your account, we will deactivate or delete your account and information from
      our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot
      problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal
      requirements.
    </p>
    <h3><u>Cookies and similar technologies:</u></h3>
    <p>
      Most Web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your
      browser to remove cookies and to reject cookies. If you choose to remove cookies or reject cookies, this
      could affect certain features or services of our Services. For further information, please see our Cookie
      Notice: <a href="/Cookie.html" target="_blank">http://litlyai.com/cookiepolicy</a>.
    </p>
    <p>
      If you have questions or comments about your privacy rights, you may email us at <strong>admin@litlyai.com</strong>.
    </p>
  `,
    DNT: `
    <p>
      Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT")
      feature or setting you can activate to signal your privacy preference not to have data about your online
      browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing
      and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals
      or any other mechanism that automatically communicates your choice not to be tracked online. If a standard
      for online tracking is adopted that we must follow in the future, we will inform you about that practice in
      a revised version of this Privacy Notice.
    </p>
    <p>
      California law requires us to let you know how we respond to web browser DNT signals. Because there currently
      is not an industry or legal standard for recognizing or honoring DNT signals, we do not respond to them at
      this time.
    </p>
  `,
    uslaws: `
    <p><strong><em>In Short: </em></strong><em>If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. More information is provided below.</em></p>

  <h3>Categories of Personal Information We Collect</h3>
  <p>The table below shows the categories of personal information we have collected in the past twelve (12) months. The table includes illustrative examples of each category and does not reflect the personal information we collect from you. For a comprehensive inventory of all personal information we process, please refer to the section “<a href="#infocollect">WHAT INFORMATION DO WE COLLECT?</a>”.</p>

  <table border="1" width="100%">
    <thead>
      <tr>
        <th width="34%">Category</th>
        <th width="52%">Examples</th>
        <th width="14%">Collected</th>
      </tr>
    </thead>
    <tbody>
      <tr><td>A. Identifiers</td><td>Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name</td><td align="center">NO</td></tr>
      <tr><td>B. Personal information as defined in the California Customer Records statute</td><td>Name, contact information, education, employment, employment history, and financial information</td><td align="center">NO</td></tr>
      <tr><td>C. Protected classification characteristics under state or federal law</td><td>Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data</td><td align="center">NO</td></tr>
      <tr><td>D. Commercial information</td><td>Transaction information, purchase history, financial details, and payment information</td><td align="center">NO</td></tr>
      <tr><td>E. Biometric information</td><td>Fingerprints and voiceprints</td><td align="center">NO</td></tr>
      <tr><td>F. Internet or other similar network activity</td><td>Browsing history, search history, online behavior, interest data, and interactions with our and other websites, applications, systems, and advertisements</td><td align="center">NO</td></tr>
      <tr><td>G. Geolocation data</td><td>Device location</td><td align="center">NO</td></tr>
      <tr><td>H. Audio, electronic, sensory, or similar information</td><td>Images and audio, video or call recordings created in connection with our business activities</td><td align="center">NO</td></tr>
      <tr><td>I. Professional or employment-related information</td><td>Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us</td><td align="center">NO</td></tr>
      <tr><td>J. Education Information</td><td>Student records and directory information</td><td align="center">NO</td></tr>
      <tr><td>K. Inferences drawn from collected personal information</td><td>Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual’s preferences and characteristics</td><td align="center">NO</td></tr>
      <tr><td>L. Sensitive personal Information</td><td></td><td align="center">NO</td></tr>
    </tbody>
  </table>

  <p>We may also collect other personal information outside of these categories through instances where you interact with us in person, online, or by phone or mail in the context of:</p>
  <ul>
    <li>Receiving help through our customer support channels;</li>
    <li>Participation in customer surveys or contests; and</li>
    <li>Facilitation in the delivery of our Services and to respond to your inquiries.</li>
  </ul>

  <p>We will use and retain the collected personal information as needed to provide the Services or for: Category H – 6 months</p>

  <h3>Sources of Personal Information</h3>
  <p>Learn more about the sources of personal information we collect in “<a href="#infocollect">WHAT INFORMATION DO WE COLLECT?</a>”.</p>

  <h3>How We Use and Share Personal Information</h3>
  <p>Learn more about how we use your personal information in the section, “<a href="#infouse">HOW DO WE PROCESS YOUR INFORMATION?</a>”.</p>
  <p>Will your information be shared with anyone else? We may disclose your personal information with our service providers pursuant to a written contract between us and each service provider. Learn more about how we disclose personal information in the section, “<a href="#whoshare">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</a>”.</p>
  <p>We may use your personal information for our own business purposes, such as for undertaking internal research for technological development and demonstration. This is not considered to be “selling” of your personal information.</p>
  <p>We have not disclosed, sold, or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months. We will not sell or share personal information in the future belonging to website visitors, users, and other consumers.</p>

  <h3>Your Rights</h3>
  <p>You have rights under certain US state data protection laws. However, these rights are not absolute, and in certain cases, we may decline your request as permitted by law. These rights include:</p>
  <ul>
    <li><strong>Right to know</strong> whether or not we are processing your personal data</li>
    <li><strong>Right to access</strong> your personal data</li>
    <li><strong>Right to correct</strong> inaccuracies in your personal data</li>
    <li><strong>Right to request</strong> the deletion of your personal data</li>
    <li><strong>Right to obtain a copy</strong> of the personal data you previously shared with us</li>
    <li><strong>Right to non-discrimination</strong> for exercising your rights</li>
    <li><strong>Right to opt out</strong> of the processing of your personal data if it is used for targeted advertising, the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects</li>
  </ul>

  <p>Depending upon the state where you live, you may also have additional rights (e.g., access to categories of data processed, lists of third parties, review of profiling, limits on sensitive data use, opt-out of certain collections, etc.) as described in the full notice.</p>

  <h3>How to Exercise Your Rights</h3>
  <p>To exercise these rights, you can contact us by visiting <a href="/privacy" target="_blank">http://litlyai.com/privacy</a>, by emailing us at hello@litlyai.com, or by referring to the contact details at the bottom of this document.</p>
  <p>Under certain US state data protection laws, you can designate an authorized agent to make a request on your behalf. We may deny a request from an authorized agent that does not submit proof that they have been validly authorized to act on your behalf in accordance with applicable laws.</p>

  <h3>Request Verification</h3>
  <p>Upon receiving your request, we will need to verify your identity to determine you are the same person about whom we have the information in our system. We will only use personal information provided in your request to verify your identity or authority to make the request. However, if we cannot verify your identity from the information already maintained by us, we may request that you provide additional information for the purposes of verifying your identity and for security or fraud-prevention purposes.</p>
  <p>If you submit the request through an authorized agent, we may need to collect additional information to verify your identity before processing your request and the agent will need to provide a written and signed permission from you to submit such request on your behalf.</p>

  <h3>Appeals</h3>
  <p>Under certain US state data protection laws, if we decline to take action regarding your request, you may appeal our decision by emailing us at admin@litlyai.com. We will inform you in writing of any action taken or not taken in response to the appeal, including a written explanation of the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state attorney general.</p>

  <h3>California “Shine The Light” Law</h3>
  <p>California Civil Code Section 1798.83, also known as the “Shine The Light” law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us by using the contact details provided in “<a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>”.</p>
`,
    otherlaws: `
    <p><strong><em>In Short: </em></strong><em>You may have additional rights based on the country you reside in.</em></p>
    <!-- ======================================================= -->
    <!-- Australia & New Zealand -->
    <!-- ======================================================= -->
    <h3>Australia and New&nbsp;Zealand</h3>
    <p>We collect and process your personal information under the obligations and conditions set by Australia’s Privacy Act&nbsp;1988 and New&nbsp;Zealand’s Privacy Act&nbsp;2020 (Privacy Act).</p>
    <p>This Privacy Notice satisfies the notice requirements defined in both Privacy Acts, in particular: what personal information we collect from you, from which sources, for which purposes, and other recipients of your personal information.</p>
    <p>If you do not wish to provide the personal information necessary to fulfil their applicable purpose, it may affect our ability to provide our services, in particular to:</p>
    <ul>
      <li>Offer you the products or services that you want</li>
      <li>Respond to or help with your requests</li>
      <li>Manage your account with us</li>
      <li>Confirm your identity and protect your account</li>
    </ul>
    <p>At any time, you have the right to request access to or correction of your personal information. You can make such a request by contacting us using the details in <a href="#request">HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</a>.</p>
    <p>If you believe we are unlawfully processing your personal information, you have the right to submit a complaint about a breach of the Australian Privacy Principles to the <a href="https://www.oaic.gov.au/privacy/privacy-complaints/lodge-a-privacy-complaint-with-us" target="_blank">Office of the Australian Information Commissioner</a> and a breach of New&nbsp;Zealand’s Privacy Principles to the <a href="https://www.privacy.org.nz/your-rights/making-a-complaint/" target="_blank">Office of the New&nbsp;Zealand Privacy Commissioner</a>.</p>
    <!-- ======================================================= -->
    <!-- Republic of South Africa -->
    <!-- ======================================================= -->
    <h3>Republic of South Africa</h3>
    <p>At any time, you have the right to request access to or correction of your personal information. You can make such a request by contacting us using the details in <a href="#request">HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</a>.</p>
    <p>If you are unsatisfied with the manner in which we address any complaint regarding our processing of personal information, you can contact the office of the regulator:</p>
    <p>
      <a href="https://inforegulator.org.za/" target="_blank">The Information Regulator (South Africa)</a><br>
      General enquiries:&nbsp;<a href="mailto:enquiries@inforegulator.org.za">enquiries@inforegulator.org.za</a><br>
      Complaints (complete POPIA/PAIA form&nbsp;5):&nbsp;
      <a href="mailto:PAIAComplaints@inforegulator.org.za">PAIAComplaints@inforegulator.org.za</a>&nbsp;&amp;&nbsp;
      <a href="mailto:POPIAComplaints@inforegulator.org.za">POPIAComplaints@inforegulator.org.za</a>
    </p>
  `,
    policyupdates: `
    <p><strong><em>In Short: </em></strong><em>Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>
    <p>
      We may update this Privacy Notice from time to time. The updated version will be indicated by an updated
      “Revised” date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may
      notify you either by prominently posting a notice of such changes or by directly sending you a notification.
      We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your
      information.
    </p>
  `,
    contact: `
    <p>
      If you have questions or comments about this notice, you may contact our Data Protection Officer (DPO)
      by email at <a href="mailto:dpo@litlyai.com">dpo@litlyai.com</a> or by post at:
    </p>
    <address>
      Litly, LLC<br>
      Data Protection Officer<br>
      3210&nbsp;171<sup>st</sup>&nbsp;Ave&nbsp;NE<br>
      Bellevue, WA&nbsp;98008<br>
      United&nbsp;States<br>
      Email: <a href="mailto:admin@litlyai.com">admin@litlyai.com</a>
    </address>
  `,
    request: `
    <p>
      You have the right to request access to the personal information we collect from you, details about how we
      have processed it, correct inaccuracies, or delete your personal information. You may also have the right to
      withdraw your consent to our processing of your personal information. These rights may be limited in some
      circumstances by applicable law.
    </p>
    <p>
      To request to review, update, or delete your personal information, please visit:&nbsp;
      <a href="/privacy" target="_blank">http://litlyai.com/privacy</a>
    </p>
  `,
};
const items = [
    {
        hash: "infocollect",
        text: "WHAT INFORMATION DO WE COLLECT?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.infocollect }),
    },
    {
        hash: "infouse",
        text: "HOW DO WE PROCESS YOUR INFORMATION?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.infouse }),
    },
    {
        hash: "legalbases",
        text: "WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.legalbases }),
    },
    {
        hash: "whoshare",
        text: "WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.whoshare }),
    },
    {
        hash: "cookies",
        text: "DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.cookies }),
    },
    {
        hash: "ai",
        text: "DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.ai }),
    },
    {
        hash: "inforetain",
        text: "HOW LONG DO WE KEEP YOUR INFORMATION?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.inforetain }),
    },
    {
        hash: "infosafe",
        text: "HOW DO WE KEEP YOUR INFORMATION SAFE?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.infosafe }),
    },
    {
        hash: "infominors",
        text: "DO WE COLLECT INFORMATION FROM MINORS?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.infominors }),
    },
    {
        hash: "privacyrights",
        text: "WHAT ARE YOUR PRIVACY RIGHTS?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.privacyrights }),
    },
    {
        hash: "DNT",
        text: "CONTROLS FOR DO-NOT-TRACK FEATURES",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.DNT }),
    },
    {
        hash: "uslaws",
        text: "DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.uslaws }),
    },
    {
        hash: "otherlaws",
        text: "DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.otherlaws }),
    },
    {
        hash: "policyupdates",
        text: "DO WE MAKE UPDATES TO THIS NOTICE?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.policyupdates }),
    },
    {
        hash: "contact",
        text: "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.contact }),
    },
    {
        hash: "request",
        text: "HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?",
        component: () => (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.request }),
    },
];
exports.PrivacyPage = (0, react_1.memo)(() => {
    const { hash } = (0, react_router_dom_1.useLocation)();
    (0, react_1.useEffect)(() => {
        if (hash) {
            const el = document.getElementById(hash.replace("#", ""));
            if (el)
                el.scrollIntoView({ behavior: "smooth" });
        }
    }, [hash]);
    return ((0, jsx_runtime_1.jsxs)(material_1.Container, { maxWidth: "lg", sx: { p: 2 }, children: [(0, jsx_runtime_1.jsx)(log_view_layout_1.LogViewLayout, { eventName: log_1.LogEventViewName.TermsPage }), (0, jsx_runtime_1.jsxs)(material_1.Stack, { spacing: 10, padding: 2, children: [(0, jsx_runtime_1.jsxs)(material_1.Stack, { spacing: 1, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "h3", children: "Privacy Policy" }), (0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body1", color: "text.secondary", children: "Last updated August\u00A018,\u00A02025" })] }), (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.intro }), (0, jsx_runtime_1.jsxs)(material_1.Stack, { spacing: 1, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "h5", children: "SUMMARY OF KEY POINTS" }), (0, jsx_runtime_1.jsx)(SectionHTML, { html: htmlSections.summary })] }), (0, jsx_runtime_1.jsxs)(material_1.Stack, { spacing: 1, id: "toc", children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "h5", children: "TABLE OF CONTENTS" }), items.map((it, idx) => ((0, jsx_runtime_1.jsx)(react_router_dom_1.Link, { to: `/privacy#${it.hash}`, children: `${idx + 1}. ${it.text}` }, it.hash)))] }), items.map((it, index) => {
                        const Section = it.component;
                        return ((0, jsx_runtime_1.jsxs)(material_1.Stack, { id: it.hash, spacing: 2, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "h5", children: `${index + 1}. ${it.text}` }), (0, jsx_runtime_1.jsx)(Section, {})] }, it.hash));
                    })] })] }));
});
exports.PrivacyPage.displayName = "PrivacyPage";
exports.default = exports.PrivacyPage;
